<template>
  <section>
    <h3 class="title">Users</h3>
    <section>
      <div class="field">
        <b-checkbox v-model="currentUser.is_active">
          Active?
        </b-checkbox>
      </div>
      <b-field grouped>
        <b-field label="First Name" expanded>
          <b-input type="text" name="name"
            v-model="currentUser.name"
            :readonly="!currentUser.is_active"
            :disabled="!currentUser.is_active"
          />
        </b-field>

        <b-field label="Last Name" expanded>
          <b-input type="text"
            v-model="currentUser.last_name"
            :readonly="!currentUser.is_active"
            :disabled="!currentUser.is_active"
          />
        </b-field>
      </b-field>

      <b-field grouped>
        <b-field label="Email" expanded>
          <b-input type="email"
            v-model="currentUser.email"
            :readonly="editMode || !currentUser.is_active"
            :disabled="editMode || !currentUser.is_active"
          >
          </b-input>
        </b-field>

        <b-field label="Role" expanded>
            <b-select expanded
              placeholder="Select a role"
              icon="account"
              v-model="currentUser.role"
              :readonly="!currentUser.is_active"
              :disabled="!currentUser.is_active"
            >
              <option
                v-for="role in roles"
                :key="role.id"
                :value="role.id"
              >{{role.name}}</option>
            </b-select>
        </b-field>
      </b-field>

      <b-field grouped>
        <b-field label="Password" expanded>
          <b-input type="password"
            v-model="currentUser.password"
            :readonly="!currentUser.is_active"
            :disabled="!currentUser.is_active"
          />
        </b-field>

        <b-field label="Confirm password" expanded>
          <b-input type="password"
            v-model="currentUser.confirm_password"
            :readonly="!currentUser.is_active"
            :disabled="!currentUser.is_active"
          />
        </b-field>
      </b-field>

      <div class="field">
        <b-checkbox
          v-model="currentUser.notifications.submit"
          :readonly="!currentUser.is_active"
          :disabled="!currentUser.is_active"
        >
          Notify form status changed to submit?
        </b-checkbox>
      </div>

      <div class="field">
        <b-checkbox
          v-model="currentUser.notifications.outstanding_kyc"
          :readonly="!currentUser.is_active"
          :disabled="!currentUser.is_active"
        >
          Notify if submitted form has outstanding KYC documents?
        </b-checkbox>
      </div>

      <div class="field">
        <b-checkbox
          v-model="currentUser.notifications.close"
          :readonly="!currentUser.is_active"
          :disabled="!currentUser.is_active"
        >
          Notify if form status changed to closed?
        </b-checkbox>
      </div>

      <div class="buttons is-right">
        <button class="button is-primary" @click.prevent="addNewUser" v-if="!editMode">Add</button>
        <button class="button" @click.prevent="clearFields" v-if="editMode">Clear</button>
        <button class="button is-danger" @click.prevent="removeUser" v-if="editMode">Remove</button>
        <button class="button is-primary" @click.prevent="saveUserData" v-if="editMode">Save</button>
      </div>
    </section>

    <section>
      <b-table :data="users"  @click="editUser" hoverable striped>
        <template slot-scope="props">
          <b-table-column label="Active?">
            <b-checkbox :value="props.row.is_active"/>
          </b-table-column>
          <b-table-column label="Email">{{props.row.email}}</b-table-column>
          <b-table-column label="Name">{{props.row.name}}</b-table-column>
          <b-table-column label="Last Name">{{props.row.last_name}}</b-table-column>
          <b-table-column label="Role">{{getRoleName(props.row.role)}}</b-table-column>
          <b-table-column label="Notify (submit)">{{props.row.notifications.submit ? 'Yes' : ''}}</b-table-column>
          <b-table-column label="Notify (outstanding)">{{props.row.notifications.outstanding_kyc ? 'Yes' : ''}}</b-table-column>
          <b-table-column label="Notify (close)">{{props.row.notifications.close ? 'Yes' : ''}}</b-table-column>
        </template>
      </b-table>
    </section>
  </section>
</template>

<script>

export default {
  name: 'UserSettings',
  data () {
    return {
      users: [],
      roles: [],
      currentUser: {
        name: null,
        last_name: null,
        email: null,
        role: 'user',
        password: null,
        confirm_password: null,
        notifications: {},
        is_active: true
      },
      editMode: false
    }
  },
  computed: {
    userRoleId () {
      if (!this.roles) return null
      const role = this.roles.filter(r => {
        if (r.name === 'user') return true
      })
      if (role.length === 0) return null
      return role[0].id
    }
  },
  created () {
    this.getRoles()
    this.getUsers()
  },
  methods: {
    getUsers () {
      this.axios.get('/account')
        .then(response => {
          if (response.data) {
            if (response.data.hasOwnProperty('Error') && response.data.Error) {
              this.$toast.open({
                message: response.data.details,
                type: 'is-info'
              })
            } else {
              this.users = response.data
            }
          }
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: 'Something went wrong while getting a list of users',
            type: 'is-danger'
          })
        })
    },
    getRoles () {
      this.axios.get('/role')
        .then(response => {
          if (response.data) {
            this.roles = response.data
            this.currentUser.role = this.userRoleId
          }
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: 'Something went wrong while getting a list of roles',
            type: 'is-danger'
          })
        })
    },
    getRoleName (roleId) {
      if (this.roles.length <= 0) return null
      const role = this.roles.filter(r => {
        if (r.id === roleId) return true
      })
      if (role.length <= 0) return null
      return role[0].name
    },
    addNewUser () {
      this.axios.post('/account', {...this.currentUser})
        .then(response => {
          // console.log(response)
          if (response.data) {
            if (!response.data.hasOwnProperty('Error')) {
              this.currentUser.password = null
              this.currentUser.confirm_password = null
              this.users.push(this.currentUser)
              this.$toast.open({
                message: 'User created',
                type: 'is-info'
              })
              this.clearFields()
            } else {
              this.$toast.open({
                message: `${response.data.msg}: ${response.data.details}`,
                type: 'is-warning'
              })
            }
          }
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: 'Something went wrong while creating a user',
            type: 'is-danger'
          })
        })
    },
    editUser (user) {
      this.editMode = true
      this.currentUser = Object.assign({}, user)
      document.querySelector('input[name="name"]').focus()
    },
    saveUserData () {
      if (this.currentUser.password !== this.currentUser.confirm_password) {
        this.$toast.open({
          message: 'Passwords don\'t match, please check',
          type: 'is-danger'
        })
        return
      }

      const data = Object.assign({}, this.currentUser)

      delete data['confirm_password']

      this.axios.put('/account', {...data})
        .then(response => {
          // console.log(response)
          if (response.data) {
            const userIdx = this.users.findIndex(u => {
              if (u.email === this.currentUser.email) {
                return true
              }
            })
            if (userIdx === -1) return
            this.$set(this.users, userIdx, response.data[0])
            this.$toast.open({
              message: 'User data updated',
              type: 'is-info'
            })
          }
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: 'Something went wrong while updating a user data',
            type: 'is-danger'
          })
        })
    },
    removeUser () {
      this.axios.delete('/account', { data: { email: this.currentUser.email } })
        .then(response => {
          // console.log(response)
          if (response.data) {
            const userIdx = this.users.findIndex(u => {
              if (u.email === this.currentUser.email) {
                return true
              }
            })

            if (userIdx === -1) return
            this.$delete(this.users, userIdx)
            this.$toast.open({
              message: 'User deleted',
              type: 'is-info'
            })
            this.clearFields()
          }
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: 'Something went wrong while deleting a user',
            type: 'is-danger'
          })
        })
    },
    clearFields () {
      this.currentUser = {
        name: null,
        last_name: null,
        email: null,
        role: this.userRoleId ? this.userRoleId : 'user',
        password: null,
        confirm_password: null,
        notifications: {},
        is_active: true
      }

      this.editMode = false
    }
  }
}
</script>

<style scoped>

.add-user {
  color: var(--main-text-color);
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 2em;
}

</style>
